import React from "react";
import { getThemeAwareColor } from "./color-utils";

export type ThemeMode = "light" | "dark";

export type ThemeContextValue = {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  toggle: () => void;
};

const ThemeContext = React.createContext<ThemeContextValue | undefined>(undefined);

const THEME_STORAGE_KEY = "creately-theme-mode";

const resolveInitialMode = (defaultMode: ThemeMode): ThemeMode => {
  if (typeof window === "undefined") {
    return defaultMode;
  }

  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === "dark" || stored === "light") {
    return stored;
  }

  return defaultMode;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode; defaultMode?: ThemeMode }> = ({
  children,
  defaultMode = "light",
}) => {
  const [mode, setMode] = React.useState<ThemeMode>(() => resolveInitialMode(defaultMode));

  React.useEffect(() => {
    const root = document.documentElement;
    if (mode === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    if (typeof window !== "undefined") {
      window.localStorage.setItem(THEME_STORAGE_KEY, mode);
    }
  }, [mode]);

  const toggle = React.useCallback(() => {
    setMode((m) => (m === "light" ? "dark" : "light"));
  }, []);

  const value = React.useMemo(() => ({ mode, setMode, toggle }), [mode, toggle]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useThemeMode = (): ThemeContextValue => {
  const ctx = React.useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useThemeMode must be used within ThemeProvider");
  }
  return ctx;
};

// Hook for getting theme-aware colors
export const useThemeAwareColor = () => {
  const { mode } = useThemeMode();
  
  return React.useCallback((color: string, options?: { type?: 'background' | 'foreground' | 'accent' }) => {
    return getThemeAwareColor(color, mode, options);
  }, [mode]);
};

